import {
  type SunlabCaseDefinition,
  SunlabsWheelConfiguration,
} from "@project/shared";
import type { SunlabsCaseType } from "../enum/SunlabsCaseType";
import type { SunlabsCaseVariant } from "../enum/SunlabsCaseVariant";
import { SunlabsConnectorType } from "../enum/SunlabsConnectorType";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { VariableOptions } from "../types/VariableOptions";
import { getCaseType } from "./CaseFactoryImporter";

export interface RequiredPreset {
  configuration: string;
  partSelection: string;
  caseType: SunlabsCaseType;
  caseVariant: SunlabsCaseVariant;
  possibleTypes: VariableOptionType[];
  pianoHinge?: boolean;
  /**
   * Variable option derived from the import (alignment, mirror, side, count, group, flip, margin, etc.).
   * Must always be taken from the import; do not use fallback defaults so the added part matches the import.
   */
  variableOptionFromImport?: VariableOptions;
}

export function getConfigurationTagsFromMegacaseInfo(
  info: SunlabCaseDefinition,
): RequiredPreset[] {
  const result: RequiredPreset[] = [];
  const { caseType, caseVariant } = getCaseType(info);
  let requiredRollerBoardTag:
    | "WheelboardWheelBraked"
    | "WheelboardWheelNonBraked"
    | null = null;
  result.push({
    caseType,
    caseVariant,
    configuration: `${SunlabsConnectorType.Lock}_${info.FastenerConfiguration}`,
    partSelection: info.Fastener,
    possibleTypes: [VariableOptionType.LOCK_SET],
  });
  for (const hinge of info.Hinges) {
    if (hinge.HingeConfiguration.indexOf("PianoHinge") !== -1) {
      continue;
    }
    const ph =
      info.Hinges.filter(
        (h) => h.HingeConfiguration.indexOf("PianoHinge") !== -1,
      ).length > 0;
    result.push({
      caseType,
      caseVariant,
      configuration: hinge.HingeConfiguration,
      partSelection: hinge.Hinge,
      possibleTypes: [VariableOptionType.HINGE_SET],
      pianoHinge: ph,
    });
  }
  if (info.Accessories) {
    for (const accessory of info.Accessories) {
      result.push({
        caseType,
        caseVariant,
        configuration: accessory.AccessoryConfiguration,
        partSelection: accessory.Accessory,
        possibleTypes: [VariableOptionType.DISHES],
      });
    }
  }
  if (info.Handles) {
    for (const handle of info.Handles) {
      result.push({
        caseType,
        caseVariant,
        configuration: handle.HandleConfiguration,
        partSelection: handle.Handle,
        possibleTypes: [VariableOptionType.HANDLES],
      });
    }
  }
  if (info.Wheels) {
    for (const wheel of info.Wheels) {
      if (wheel.WheelConfiguration.match(/Wheelboard/) !== null) {
        if (
          wheel.WheelConfiguration.startsWith("WheelboardWheelBraked") &&
          requiredRollerBoardTag === null
        ) {
          requiredRollerBoardTag = "WheelboardWheelBraked";
        } else if (
          wheel.WheelConfiguration.startsWith("WheelboardWheelNonBraked") &&
          requiredRollerBoardTag === null
        ) {
          requiredRollerBoardTag = "WheelboardWheelNonBraked";
        }
        continue;
      }
      if (
        wheel.WheelConfiguration ===
        SunlabsWheelConfiguration.Wheel_WithTrolleyFunction
      ) {
        continue;
      }
      result.push({
        caseType,
        caseVariant,
        configuration: wheel.WheelConfiguration,
        partSelection: wheel.Wheel,
        possibleTypes: [
          VariableOptionType.FEET,
          VariableOptionType.RUNNERS,
          VariableOptionType.TROLLEY_HANDLES,
        ],
      });
    }
  }

  if (requiredRollerBoardTag) {
    result.push({
      caseType,
      caseVariant,
      configuration: requiredRollerBoardTag,
      // Roller board presets are identified by tags; no stable partSelection in data.
      partSelection: "",
      possibleTypes: [VariableOptionType.ROLLER_BOARD],
    });
  }
  return result;
}
